import { Injectable } from '@angular/core'
import { ActivatedRouteSnapshot, Resolve, Router } from '@angular/router'

import { LaskuService } from '../service/lasku/lasku.service'

import { AsiakkaanTuote, Lasku, LaskuBase, Laskuasetukset, LaskunAsiakas, LaskunumeroTyyppi, UUDEN_LASKUN_AVAIN } from '@lemon/core-firebase/model/lasku'
import { LaskuSharedService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'

import { LaskuttavaAsiakasProvider } from 'app/_jaettu-angular/service/lasku/laskuttava-asiakas.service'
import { Observable, firstValueFrom, from, of as observableOf } from 'rxjs'
import { map, mergeMap, switchMap, take } from 'rxjs/operators'
import { LaskutusprofiiliService } from '../service/lasku/laskutusprofiili.service'

export interface LaskuKatseleComponentData {
  juurilasku: Lasku
  kasiteltava: LaskuBase
  asetukset: Laskuasetukset
  tulosta: boolean
}

export interface LaskuKatseleComponentExistingData {
  juurilasku: Lasku
  kasiteltava: LaskuBase
}

export interface LaskuComponentExistingData {
  juurilasku: Lasku
  asetukset: Laskuasetukset
}

export interface LaskuComponentData {
  juurilasku: Lasku
  kasiteltava: LaskuBase
  asetukset: Laskuasetukset
}

export interface LaskutComponentData {
  nollaaHakuasetukset: boolean
}

@Injectable()
export class LaskuComponentDataResolve implements Resolve<LaskuComponentData> {
  private existingData: LaskuComponentExistingData = null

  constructor(
    private router: Router,
    private laskuService: LaskuService,
    private laskuKopioija: LaskuKopioija,
    private laskuSharedService: LaskuSharedService,
    private laskuttavaAsiakasProvider: LaskuttavaAsiakasProvider,
    private _laskutusprofiiliService: LaskutusprofiiliService,
  ) {}

  asetaOlemassaolevaData(data: LaskuComponentExistingData) {
    this.existingData = data
  }

  resolve(route: ActivatedRouteSnapshot): Observable<LaskuComponentData> {
    const id = route.params['id']
    const kasiteltavaAvain = route.params['kasiteltavaAvain']
    if (id === UUDEN_LASKUN_AVAIN) {
      return from(this.laskuKopioija.annaUusiLasku()).pipe(
        switchMap(lasku => this.haeOletusAsetukset({ juurilasku: lasku, kasiteltava: lasku })),
        map(data => {
          if (data?.asetukset?.oletusMaksuaikaa) {
            this.laskuKopioija.asetaUudenLaskunMaksuaika(data.juurilasku, data.asetukset.oletusMaksuaikaa)
          }
          return data
        }),
      )
    } else if (this.existingData != null && this.existingData.juurilasku && this.existingData.juurilasku.avain === id) {
      return this.hoidaMuokattavat(this.existingData.juurilasku, kasiteltavaAvain)
    } else {
      return this.laskuService.getLaskuObservable(id).pipe(
        take(1),
        mergeMap(juurilasku => {
          if (!juurilasku) {
            this.router.navigate(['/laskutus/laskut'])
            return observableOf(null)
          }

          return this.hoidaMuokattavat(juurilasku, kasiteltavaAvain)
        }),
      )
    }
  }

  private hoidaMuokattavat(juurilasku: Lasku, kasiteltavaAvain: string): Observable<LaskuComponentData> {
    const edellinen = this.laskuSharedService.annaViimeisinKasiteltavaLasku(juurilasku)
    return this.haeAsiakas(edellinen).pipe(
      mergeMap(asiakas => {
        const viimeinenTavallinen = this.laskuSharedService.annaViimeisinTavallinenLasku(juurilasku)
        if (kasiteltavaAvain === 'kopioiuusi') {
          return from(
            this.laskuKopioija.kopioiUusiLasku(juurilasku, viimeinenTavallinen, asiakas, {
              onkoLaskunLuonutAsiakasOssRekisterissa: (): Promise<boolean> => {
                return firstValueFrom(this.laskuttavaAsiakasProvider.onkoOssRekisterissa())
              },
            }),
          ).pipe(
            mergeMap(uusiJuurilasku => {
              for (const tuote of uusiJuurilasku.tuotteet ?? []) {
                tuote.tallennetaanRekisteriin = false
              }
              return this.haeOletusAsetukset({ juurilasku: uusiJuurilasku, kasiteltava: uusiJuurilasku })
            }),
          )
        }

        if (kasiteltavaAvain === 'hyvita') {
          const muokattavaksiJuuri = this.laskuKopioija.copyLasku(juurilasku)
          const muokattavaksiKasiteltava = this.laskuKopioija.kopioiLaskuHyvitettavaksi(viimeinenTavallinen, asiakas)
          this.laskuSharedService.lisaaLiittyviinLaskuihinIlmanMuutostenKeraamista(muokattavaksiJuuri, muokattavaksiKasiteltava)
          return this._haeAlkuperaisenLaskunAsetukset(viimeinenTavallinen, { juurilasku: muokattavaksiJuuri, kasiteltava: muokattavaksiKasiteltava })
        }
        if (kasiteltavaAvain === UUDEN_LASKUN_AVAIN) {
          const muokattavaksiJuuri = this.laskuKopioija.copyLasku(juurilasku)
          const muokattavaksiKasiteltava = this.laskuKopioija.kopioiLaskuMuokattavaksi(viimeinenTavallinen, asiakas)
          this.laskuSharedService.lisaaLiittyviinLaskuihinIlmanMuutostenKeraamista(muokattavaksiJuuri, muokattavaksiKasiteltava)
          return this._haeAlkuperaisenLaskunAsetukset(viimeinenTavallinen, { juurilasku: muokattavaksiJuuri, kasiteltava: muokattavaksiKasiteltava })
        }
        const kasiteltava = this.laskuSharedService.annaKasiteltavaLasku(juurilasku, kasiteltavaAvain)
        if (kasiteltava && this.laskuSharedService.onkoKasiteltavaLuonnos(juurilasku, kasiteltava)) {
          const kopioidut = this.laskuKopioija.kopioiEiLahetettyMuokattavaksi(juurilasku, kasiteltava, asiakas)
          const onkoHyvityksenTaiKorjauksenLuonnos = kasiteltava.avain !== juurilasku.avain
          if (onkoHyvityksenTaiKorjauksenLuonnos) {
            return this._haeAlkuperaisenLaskunAsetukset(kasiteltava, { juurilasku: kopioidut.muokattavaJuuri, kasiteltava: kopioidut.muokattavaKasiteltava })
          }
          return this.haeOletusAsetukset({ juurilasku: kopioidut.muokattavaJuuri, kasiteltava: kopioidut.muokattavaKasiteltava })
        } else if ((kasiteltava && kasiteltava.nrotyyppi === LaskunumeroTyyppi.KORJAUS) || kasiteltava.nrotyyppi === LaskunumeroTyyppi.TAVALLINEN) {
          const muokattavaksiJuuri = this.laskuKopioija.copyLasku(juurilasku)
          const muokattavaksiKasiteltava = this.laskuKopioija.kopioiLaskuMuokattavaksi(viimeinenTavallinen, asiakas)
          this.laskuSharedService.lisaaLiittyviinLaskuihinIlmanMuutostenKeraamista(muokattavaksiJuuri, muokattavaksiKasiteltava)
          return this._haeAlkuperaisenLaskunAsetukset(viimeinenTavallinen, { juurilasku: muokattavaksiJuuri, kasiteltava: muokattavaksiKasiteltava })
        } else if (kasiteltava && kasiteltava.nrotyyppi === LaskunumeroTyyppi.HYVITYS) {
          const kopioidut = this.laskuKopioija.kopioiEiLahetettyMuokattavaksi(juurilasku, kasiteltava, asiakas)
          return this._haeAlkuperaisenLaskunAsetukset(viimeinenTavallinen, { juurilasku: kopioidut.muokattavaJuuri, kasiteltava: kopioidut.muokattavaKasiteltava })
        }

        this.router.navigate(['/laskutus/laskut', juurilasku.avain, viimeinenTavallinen.avain])
        return observableOf(null)
      }),
      take(1),
    )
  }

  private _haeAlkuperaisenLaskunAsetukset(alkuperainenLasku: LaskuBase, existingLukittuData: LaskuKatseleComponentExistingData): Observable<LaskuComponentData> {
    if (!alkuperainenLasku.asetukset) {
      return this.haeOletusAsetukset(existingLukittuData)
    }
    const data: LaskuComponentData = {
      asetukset: alkuperainenLasku.asetukset,
      juurilasku: existingLukittuData.juurilasku,
      kasiteltava: existingLukittuData.kasiteltava,
    }
    return observableOf(data)
  }

  private haeAsiakas(kasiteltava: LaskuBase): Observable<LaskunAsiakas | null> {
    if (kasiteltava && kasiteltava.asiakas && kasiteltava.asiakas.avain) {
      return this.laskuService.getAsiakasObservable(kasiteltava.asiakas.avain)
    }
    return observableOf(null)
  }

  private haeOletusAsetukset(existingLukittuData: LaskuKatseleComponentExistingData): Observable<LaskuComponentData> {
    return this._laskutusprofiiliService.annaLaskutusprofiiliObservable(existingLukittuData.kasiteltava.asiakas?.laskutusprofiiliAvain).pipe(
      map(asetukset => {
        if (asetukset) {
          const data: LaskuComponentData = {
            asetukset: asetukset,
            juurilasku: existingLukittuData.juurilasku,
            kasiteltava: existingLukittuData.kasiteltava,
          }
          return data
        }
        this.router.navigate(['/laskutus/laskut'])
        return null
      }),
    )
  }
}

@Injectable()
export class LaskuKatseleComponentDataResolve implements Resolve<LaskuKatseleComponentData> {
  private existingData: LaskuKatseleComponentExistingData = null

  constructor(
    private router: Router,
    private laskuService: LaskuService,
    private laskuKopioija: LaskuKopioija,
    private laskuSharedService: LaskuSharedService,
    private _laskutusprofiiliService: LaskutusprofiiliService,
  ) {}

  asetaOlemassaolevaData(data: LaskuKatseleComponentExistingData) {
    this.existingData = data
  }

  resolve(route: ActivatedRouteSnapshot): Observable<LaskuKatseleComponentData> {
    const id = route.params['id']
    const kasiteltavaAvain = route.params['kasiteltavaAvain']
    const toiminto = route.params['toiminto']
    if (id === 'uusi') {
      return from(this.laskuKopioija.annaUusiLasku()).pipe(switchMap(lasku => this.haeOletusAsetukset({ juurilasku: lasku, kasiteltava: lasku }, toiminto)))
    } else if (
      this.existingData != null &&
      this.existingData.juurilasku &&
      this.existingData.kasiteltava &&
      this.existingData.juurilasku.avain === id &&
      this.existingData.kasiteltava.avain === kasiteltavaAvain
    ) {
      return this.haeLaskunLaskuAsetukset(this.existingData, toiminto)
    } else {
      return this.laskuService.getLaskuObservable(id).pipe(
        take(1),
        mergeMap(juurilasku => {
          if (!juurilasku) {
            this.router.navigate(['/laskutus/laskut'])
            return observableOf(null)
          }

          if (kasiteltavaAvain === 'uusi') {
            const edellinen = this.laskuSharedService.annaViimeisinKasiteltavaLasku(juurilasku)
            const kasiteltava = this.laskuKopioija.kopioiLaskuMuokattavaksi(edellinen, null)
            this.laskuSharedService.lisaaLiittyviinLaskuihinIlmanMuutostenKeraamista(juurilasku, kasiteltava)
            return this.haeLaskunLaskuAsetukset({ juurilasku: juurilasku, kasiteltava: kasiteltava }, toiminto)
          } else {
            const kasiteltava = this.laskuSharedService.annaKasiteltavaLasku(juurilasku, kasiteltavaAvain)
            return this.haeLaskunLaskuAsetukset({ juurilasku: juurilasku, kasiteltava: kasiteltava }, toiminto)
          }
        }),
      )
    }
  }

  private haeLaskunLaskuAsetukset(existingLukittuData: LaskuKatseleComponentExistingData, toiminto: string): Observable<LaskuKatseleComponentData> {
    // Toistuvan laskun aihioon tallennetut asetukset ovat vanha kopio, ja lähetys käyttää
    // aina tuoreita laskuasetuksia, joten myös esikatselun pitää näyttää tuoreet
    if (existingLukittuData.kasiteltava.asetukset && !existingLukittuData.juurilasku?.toistuvaLaskuaihio) {
      const data: LaskuKatseleComponentData = {
        asetukset: existingLukittuData.kasiteltava.asetukset,
        juurilasku: existingLukittuData.juurilasku,
        kasiteltava: existingLukittuData.kasiteltava,
        tulosta: toiminto === 'tulosta',
      }
      return observableOf(data)
    }
    return this.haeOletusAsetukset(existingLukittuData, toiminto)
  }

  private haeOletusAsetukset(existingLukittuData: LaskuKatseleComponentExistingData, toiminto: string): Observable<LaskuKatseleComponentData> {
    return this._laskutusprofiiliService.annaLaskutusprofiiliObservable(existingLukittuData.kasiteltava.asiakas?.laskutusprofiiliAvain).pipe(
      map(asetukset => {
        if (asetukset) {
          const data: LaskuKatseleComponentData = {
            asetukset: asetukset,
            juurilasku: existingLukittuData.juurilasku,
            kasiteltava: existingLukittuData.kasiteltava,
            tulosta: toiminto === 'tulosta',
          }
          return data
        }
        this.router.navigate(['/laskutus/laskut'])
        return null
      }),
    )
  }
}

@Injectable()
export class TuoteComponentDataResolve implements Resolve<AsiakkaanTuote> {
  private existingData: AsiakkaanTuote = null

  constructor(
    private laskuService: LaskuService,
    private laskuKopioija: LaskuKopioija,
  ) {}

  asetaOlemassaolevaData(data: AsiakkaanTuote) {
    this.existingData = data
  }

  resolve(route: ActivatedRouteSnapshot): Observable<AsiakkaanTuote> {
    const id = route.params['id']
    if (id === 'uusi') {
      return observableOf(this.laskuKopioija.annaUusiTuote())
    } else if (this.existingData != null && this.existingData.$key === id) {
      return observableOf(this.existingData)
    } else {
      return this.laskuService.getTuoteObservable(id).pipe(take(1))
    }
  }
}

@Injectable()
export class AsiakasComponentDataResolve implements Resolve<LaskunAsiakas> {
  private existingData: LaskunAsiakas = null

  constructor(
    private laskuService: LaskuService,
    private laskuKopioija: LaskuKopioija,
  ) {}

  asetaOlemassaolevaData(data: LaskunAsiakas) {
    this.existingData = data
  }

  resolve(route: ActivatedRouteSnapshot): Observable<LaskunAsiakas> {
    const id = route.params['id']
    if (id === 'uusi') {
      return observableOf(this.laskuKopioija.annaUusiAsiakas())
    } else if (this.existingData != null && this.existingData.avain === id) {
      return observableOf(this.existingData)
    } else {
      return this.laskuService.getAsiakasObservable(id).pipe(take(1))
    }
  }
}

@Injectable()
export class LaskutComponentDataResolve implements Resolve<LaskutComponentData> {
  private existingData: LaskutComponentData = null

  constructor() {}

  asetaOlemassaolevaData(data: LaskutComponentData) {
    this.existingData = data
  }

  resolve(route: ActivatedRouteSnapshot): Observable<LaskutComponentData> {
    if (this.existingData) {
      const existing = this.existingData
      this.existingData = null
      return observableOf(existing)
    } else {
      return observableOf(null)
    }
  }
}
